import { TRACKING_ENABLED, isTrackingAllowedOnCurrentHost } from "../config/tracking.js";
import { sanitizeEventParams, sanitizeUrl } from "./sanitize.js";
import { getConsentState, hasConsent } from "./consent.js";

const isBrowser = () => typeof window !== "undefined";
const EVENT_SCHEMA_VERSION = "1.0";

function isEventAllowed(event) {
  const state = getConsentState();
  if (!state.known) return false;
  // A conversion can be useful to either measurement (GA4) or advertising.
  // All behavioural/engagement events require the analytics purpose.
  if (["generate_lead", "application_submit"].includes(event)) {
    return state.analytics || state.marketing;
  }
  return state.analytics;
}

function getDataLayer(event) {
  if (
    !TRACKING_ENABLED ||
    !isBrowser() ||
    !isTrackingAllowedOnCurrentHost() ||
    !isEventAllowed(event)
  ) {
    return null;
  }
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

// Un errore del tracking non deve mai propagarsi al chiamante: i form usano il
// valore di ritorno per sapere se l'evento e' partito, non per decidere se
// l'invio e' riuscito.
function pushEvent(dl, payload) {
  try {
    dl.push(payload);
    return true;
  } catch {
    return false;
  }
}

function currentPageContext() {
  if (!isBrowser()) return {};
  const { pathname, search, href } = window.location;
  const pageLocation = sanitizeUrl(href, { allowCampaignQuery: true });
  const pagePath = sanitizeUrl(`${pathname}${search}`, { allowCampaignQuery: true });
  return {
    page_path: pagePath,
    page_location: pageLocation,
    page_title: typeof document.title === "string" ? document.title.slice(0, 160) : undefined,
    page_language: /^(it|en)(?:-[A-Z]{2})?$/.test(document.documentElement.lang || "")
      ? document.documentElement.lang
      : undefined,
  };
}

export function trackEvent(event, params = {}) {
  const dl = getDataLayer(event);
  if (!dl) return false;
  const safeParams = sanitizeEventParams(params, { allowReserved: true });
  delete safeParams.event;
  delete safeParams.schema_version;
  delete safeParams.page_location;
  delete safeParams.page_path;
  delete safeParams.page_title;
  delete safeParams.page_language;
  return pushEvent(dl, {
    event,
    schema_version: EVENT_SCHEMA_VERSION,
    ...safeParams,
    ...currentPageContext(),
  });
}

// Restituisce true solo se l'evento e' finito nel dataLayer: chi chiama puo'
// riprovare quando il consenso arriva dopo il primo render.
export function trackPageView({ path, title, referrer } = {}) {
  const dl = getDataLayer("pageview");
  if (!dl) return false;
  const ctx = currentPageContext();
  return pushEvent(dl, {
    event: "pageview",
    schema_version: EVENT_SCHEMA_VERSION,
    ...ctx,
    ...(path ? { page_path: sanitizeUrl(path, { allowCampaignQuery: true }) } : {}),
    ...(title ? { page_title: title.slice(0, 160) } : {}),
    ...(referrer ? { page_referrer: sanitizeUrl(referrer) } : {}),
  });
}

function normalizeEmail(v) {
  if (typeof v !== "string") return "";
  const value = v.trim().toLowerCase();
  const at = value.lastIndexOf("@");
  if (at < 1) return value;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  const normalizedLocal =
    domain === "gmail.com" || domain === "googlemail.com" ? local.replace(/\./g, "") : local;
  return `${normalizedLocal}@${domain}`;
}

function normalizeName(v) {
  return typeof v === "string" ? v.trim().toLowerCase() : "";
}

function normalizePhone(v) {
  if (typeof v !== "string") return "";
  const trimmed = v.trim();
  if (!trimmed.startsWith("+")) return "";
  const digits = trimmed.slice(1).replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return "";
  return `+${digits}`;
}

async function sha256Hex(input) {
  if (!input || !isBrowser() || !window.crypto?.subtle) return undefined;
  try {
    const buf = new TextEncoder().encode(input);
    const digest = await window.crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return undefined;
  }
}

function splitFullName(full) {
  const t = (full || "").trim();
  if (!t) return { firstName: "", lastName: "" };
  const idx = t.indexOf(" ");
  if (idx === -1) return { firstName: t, lastName: "" };
  return { firstName: t.slice(0, idx), lastName: t.slice(idx + 1).trim() };
}

export function trackFormStart({ formType, formId }) {
  trackEvent("form_start", { form_id: formId || formType, form_type: formType });
}

export function trackFormSubmitAttempt({ formType, formId }) {
  trackEvent("form_submit_attempt", { form_id: formId || formType, form_type: formType });
}

export async function trackFormSubmit({ formType, formId, values = {}, extra = {}, eventName }) {
  const dl = eventName ? getDataLayer(eventName) : null;
  if (!dl) return false;
  // La navigazione verso la thank-you page puo' partire appena l'hashing e'
  // concluso: congela il contesto della pagina del form prima di ogni await.
  const pageContext = currentPageContext();

  let firstName = values.firstName || values.nome || "";
  let lastName = values.lastName || values.cognome || "";
  if (!firstName && !lastName && values.name) {
    const split = splitFullName(values.name);
    firstName = split.firstName;
    lastName = split.lastName;
  }
  const email = values.email || "";
  const phone = values.phone || values.telefono || "";

  const emailNorm = normalizeEmail(email);
  const firstNameNorm = normalizeName(firstName);
  const lastNameNorm = normalizeName(lastName);
  const phoneNorm = normalizePhone(phone);

  const [emailSha, firstNameSha, lastNameSha, phoneSha] = await Promise.all([
    sha256Hex(emailNorm),
    sha256Hex(firstNameNorm),
    sha256Hex(lastNameNorm),
    sha256Hex(phoneNorm),
  ]);

  const user_data = {};
  if (emailSha) user_data.email_sha256 = emailSha;
  if (firstNameSha) user_data.first_name_sha256 = firstNameSha;
  if (lastNameSha) user_data.last_name_sha256 = lastNameSha;
  if (phoneSha) user_data.phone_sha256 = phoneSha;
  // Stessi hash, anche con i nomi dello schema di Google: il tag Google Ads
  // server-side legge user_data da solo, ma solo con queste chiavi. Le chiavi
  // sopra restano perche' i tag LinkedIn del container server le mappano a mano.
  // ponytail: la coppia serve finche' la variabile server legge
  // `user_data.email_sha256`. Tolte le storiche quando viene ripuntata.
  if (emailSha) user_data.sha256_email_address = emailSha;
  if (phoneSha) user_data.sha256_phone_number = phoneSha;
  if (firstNameSha || lastNameSha) {
    user_data.address = {
      ...(firstNameSha ? { sha256_first_name: firstNameSha } : {}),
      ...(lastNameSha ? { sha256_last_name: lastNameSha } : {}),
    };
  }

  const payload = {
    event: eventName,
    schema_version: EVENT_SCHEMA_VERSION,
    ...pageContext,
    form_id: formId || formType,
    form_type: formType,
    ...sanitizeEventParams(extra),
  };
  // Recruiting non deve entrare nel percorso enhanced conversions commerciale.
  if (eventName !== "application_submit" && hasConsent("marketing")) {
    payload.user_data = user_data;
  }
  return pushEvent(dl, payload);
}

export function trackCtaClick({ ctaId, ctaLabel, ctaLocation, ctaDestination, extra = {} }) {
  trackEvent("cta_click", {
    cta_id: ctaId,
    cta_label: ctaLabel,
    cta_location: ctaLocation,
    cta_destination: ctaDestination,
    ...sanitizeEventParams(extra),
  });
}

export function trackConfiguratorStart({ configuratorId, totalSteps }) {
  trackEvent("configurator_start", {
    configurator_id: configuratorId,
    total_steps: totalSteps,
  });
}

export function trackConfiguratorAnswer({
  configuratorId,
  questionId,
  answerId,
  stepIndex,
  totalSteps,
}) {
  trackEvent("configurator_answer", {
    configurator_id: configuratorId,
    question_id: questionId,
    answer_id: answerId,
    step_index: stepIndex,
    total_steps: totalSteps,
  });
}

export function trackConfiguratorComplete({
  configuratorId,
  recommendedTier,
  configuratorSelection,
  totalSteps,
}) {
  trackEvent("configurator_complete", {
    configurator_id: configuratorId,
    recommended_tier: recommendedTier,
    configurator_selection: configuratorSelection,
    total_steps: totalSteps,
  });
}

export function trackConfiguratorReset({ configuratorId, stepIndex }) {
  trackEvent("configurator_reset", {
    configurator_id: configuratorId,
    step_index: stepIndex,
  });
}

export function trackConfiguratorAbandon({ configuratorId, stepIndex, stepLabel, totalSteps }) {
  trackEvent("configurator_abandon", {
    configurator_id: configuratorId,
    step_index: stepIndex,
    step_label: stepLabel,
    total_steps: totalSteps,
  });
}

export function trackScroll75() {
  trackEvent("scroll75", { scroll_depth: 75 });
}

export function trackOutboundClick({ url, kind = "outbound", linkText }) {
  trackEvent("outbound_click", {
    outbound_url: sanitizeUrl(url),
    outbound_kind: kind,
    link_text: linkText,
  });
}

export function trackFileDownload({ url, fileExtension, linkText }) {
  trackEvent("file_download", {
    file_url: sanitizeUrl(url),
    file_extension: fileExtension,
    link_text: linkText,
  });
}

export function track404View({ requestedPath, referrer }) {
  trackEvent("view_404", {
    requested_path: sanitizeUrl(requestedPath, { allowCampaignQuery: true }),
    page_referrer: sanitizeUrl(referrer),
  });
}

export function trackLanguageSwitch({ from, to }) {
  trackEvent("language_switch", { lang_from: from, lang_to: to });
}

export function trackFormError({ formType, formId, field, errorCode, httpStatus }) {
  trackEvent("form_error", {
    form_id: formId || formType,
    form_type: formType,
    error_field: field,
    error_code: errorCode,
    http_status: httpStatus,
  });
}

export function trackTurnstileFailure({ formType, reason }) {
  trackEvent("turnstile_failure", { form_type: formType, reason });
}

export function trackClickToCall({ linkText }) {
  trackEvent("click_to_call", {
    cta_id: "contact_phone",
    link_text: linkText,
  });
}

export function trackMailtoClick({ linkText }) {
  trackEvent("mailto_click", {
    cta_id: "contact_email",
    link_text: linkText,
  });
}
